import React, { useEffect, useRef, useState } from 'react';
import { CoinIcon, DENOM_COLORS, type CoinDenom } from '../CoinAmount';
import { coinParts, formatCoinFull } from '../../utils/coin';
import { useCoinRewardStore, type CoinGrant } from '../../stores/coinRewardStore';
import { useCoinGrants } from '../../hooks/useCoinGrants';
import { useProgressDisplayStore } from '../../stores/progressDisplayStore';
import { useXpReward } from '../../hooks/useXpReward';

/**
 * Sequence (ms from the card appearing). The first three numbers are the XP
 * reward's own — a 260ms rise and a ~1.1s hold — because the two cards share a
 * strip of screen and should feel like one family. What follows is the part XP
 * has no equivalent of: the coin leaves.
 */
const HOLD_UNTIL = 1960;   // the coin lifts out of the card
const CARD_OUT_AT = 2000;  // 40ms later, so the card never covers the flight
const LAND_AT = 2420;      // the coin reaches the wallet
const DONE = 2900;

/** No spin, no flight — the card simply appears, is read, and goes. */
const REDUCED_OUT_AT = 2600;
const REDUCED_DONE = 2920;

/**
 * Titles, and what to say when the ledger row's own note is empty.
 *
 * ⚠️ TOTAL over every kind, including the debits that can never get here: a
 * partial map would render `undefined` the day a migration adds a kind, and
 * the wallet's own type is a hand-maintained mirror with nothing checking it.
 */
const LABELS: Record<CoinGrant['kind'], { title: string; fallback: string }> = {
  welcome_bundle: { title: 'Welcome bundle', fallback: 'Your starting balance' },
  monthly_grant: { title: 'Monthly allowance', fallback: 'Added to your wallet' },
  gift: { title: 'A gift', fallback: 'From Metanoia' },
  marketplace_sale: { title: 'Marketplace sale', fallback: 'Someone bought your item' },
  topup: { title: 'Top-up complete', fallback: 'Added to your wallet' },
  adjustment: { title: 'Added to your wallet', fallback: 'From the Metanoia team' },
  ai_spend: { title: 'Added to your wallet', fallback: '' },
  marketplace_buy: { title: 'Added to your wallet', fallback: '' },
};

/**
 * Where the coin is going.
 *
 * ⚠️⚠️ MEASURED, NEVER ASSUMED. The desktop rail slides between 64px and 192px
 * wide, and the wallet is reveal-gated, so its position is not a constant and
 * may not exist at all. On a phone the rail is not rendered and the wallet
 * lives inside the drawer, so the menu button stands in for it. Exactly one of
 * the two is ever drawn, which is why the first one with a real box wins.
 */
function findCoinTarget(): DOMRect | null {
  const els = Array.from(document.querySelectorAll<HTMLElement>('[data-coin-target]'));
  for (const el of els) {
    const r = el.getBoundingClientRect();
    if (r.width > 0 && r.height > 0) return r;
  }
  return null;
}

function useMediaMatch(query: string): boolean {
  const supported = typeof window !== 'undefined' && typeof window.matchMedia === 'function';
  const [matches, setMatches] = useState(() => (supported ? window.matchMedia(query).matches : false));
  useEffect(() => {
    if (!supported) return;
    const mq = window.matchMedia(query);
    const sync = () => setMatches(mq.matches);
    sync();
    mq.addEventListener?.('change', sync);
    return () => mq.removeEventListener?.('change', sync);
  }, [query, supported]);
  return matches;
}

/** The coin itself, at 40px — the only thing that flies. */
function Medallion({ denom }: { denom: CoinDenom }) {
  const c = DENOM_COLORS[denom];
  return (
    <svg width="40" height="40" viewBox="0 0 20 20" aria-hidden="true" className="block">
      <circle cx="10" cy="10" r="9" fill={c.fill} stroke={c.rim} strokeWidth="1.6" />
      <circle cx="10" cy="10" r="5.6" fill="none" stroke={c.rim} strokeWidth="1" opacity="0.55" />
      <path d="M 5.2 6.4 A 6 6 0 0 1 9 4.2" fill="none" stroke={c.shine} strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

/**
 * Coin arriving, said out loud: a card at the bottom of the screen naming what
 * the money was for, and a coin that leaves it for the wallet.
 *
 * ⚠️ One card at a time, and the XP toast owns this strip first. Two cards in
 * one place is a collision, and a coin card that shoulders XP aside loses both
 * messages — so when the XP toast is switched on, sitting at the bottom, and
 * wide enough to be drawn, coin waits its turn. Everywhere else there is
 * nothing to wait for.
 */
export function CoinRewardToast() {
  const current = useCoinRewardStore((s) => s.current);
  const queued = useCoinRewardStore((s) => s.queue.length);
  const advance = useCoinRewardStore((s) => s.advance);
  const finish = useCoinRewardStore((s) => s.finish);

  const xp = useXpReward();
  const xpToastOn = useProgressDisplayStore((s) => s.desktopSurfaces.includes('toast'));
  const xpAtBottom = useProgressDisplayStore((s) => s.toastPosition === 'bottom');
  const wide = useMediaMatch('(min-width: 1024px)');
  const reduced = useMediaMatch('(prefers-reduced-motion: reduce)');
  const laneBusy = xp.active && xpToastOn && xpAtBottom && wide;

  const [out, setOut] = useState(false);
  const [flight, setFlight] = useState<{ dx: number; dy: number } | null>(null);
  const [ring, setRing] = useState<DOMRect | null>(null);
  const coinRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (current || laneBusy || queued === 0) return;
    advance();
  }, [current, laneBusy, queued, advance]);

  useEffect(() => {
    if (!current) return;
    setOut(false);
    setFlight(null);
    setRing(null);

    const timers: number[] = [];
    if (reduced) {
      timers.push(window.setTimeout(() => setOut(true), REDUCED_OUT_AT));
      timers.push(window.setTimeout(() => finish(), REDUCED_DONE));
    } else {
      timers.push(
        window.setTimeout(() => {
          const target = findCoinTarget();
          const from = coinRef.current?.getBoundingClientRect();
          // No wallet on screen — no flight, and no fake one either. The card
          // simply says its piece and goes.
          if (!target || !from) return;
          setFlight({
            dx: target.left + target.width / 2 - (from.left + from.width / 2),
            dy: target.top + target.height / 2 - (from.top + from.height / 2),
          });
        }, HOLD_UNTIL),
      );
      timers.push(window.setTimeout(() => setOut(true), CARD_OUT_AT));
      // Measured again on arrival rather than reused: 460ms is long enough for
      // the rail to have collapsed under the user's hand.
      timers.push(window.setTimeout(() => setRing(findCoinTarget()), LAND_AT));
      timers.push(window.setTimeout(() => finish(), DONE));
    }
    return () => timers.forEach((t) => clearTimeout(t));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current?.id, reduced]);

  if (!current) return null;

  const label = LABELS[current.kind] ?? { title: 'Added to your wallet', fallback: '' };
  const subtitle = current.note?.trim() || label.fallback;
  const parts = coinParts(current.amountCp);
  const flightStyle = flight
    ? ({ ['--coin-dx' as string]: `${flight.dx}px`, ['--coin-dy' as string]: `${flight.dy}px` } as React.CSSProperties)
    : undefined;

  return (
    <>
      <div className="pointer-events-none fixed bottom-24 left-1/2 z-50 -translate-x-1/2 lg:bottom-5">
        <div className="relative">
          <div
            className={`coin-card ${out ? 'is-out' : ''} w-[22rem] max-w-[calc(100vw-2rem)] rounded-xl border border-border bg-card/95 p-3.5 backdrop-blur shadow-[0_10px_44px_-6px_rgba(0,0,0,0.45)] dark:shadow-[0_10px_44px_-6px_rgba(255,255,255,0.22)]`}
            aria-hidden="true"
          >
            <div className="flex items-center gap-3">
              {/* The coin's seat. It is drawn outside the card, so that it can
                  leave without fading away with it. */}
              <div className="h-10 w-10 shrink-0" />
              <div className="min-w-0">
                <p className="text-sm font-semibold text-foreground">{label.title}</p>
                {subtitle && <p className="truncate text-[11px] text-muted-foreground">{subtitle}</p>}
              </div>
            </div>
            <p className="mt-2.5 flex items-center gap-2 pl-[3.25rem] text-lg font-semibold tabular-nums text-foreground">
              <span className="text-amber-400">+</span>
              {parts.map((p) => (
                <span key={p.denom} className="inline-flex items-center gap-x-1">
                  <CoinIcon denom={p.denom} />
                  <span>{p.value}</span>
                </span>
              ))}
            </p>
          </div>

          {/* A trail, one frame behind — rendered only while the coin is moving,
              or it would sit as a second coin stacked on the first. */}
          {flight && (
            <div className="coin-flight is-ghost pointer-events-none absolute left-3.5 top-3.5 h-10 w-10" style={flightStyle} aria-hidden="true">
              <Medallion denom={parts[0].denom} />
            </div>
          )}

          <div
            ref={coinRef}
            className={`pointer-events-none absolute left-3.5 top-3.5 h-10 w-10 ${flight ? 'coin-flight' : ''}`}
            style={flightStyle}
            aria-hidden="true"
          >
            <div className="coin-medallion-holder h-10 w-10">
              <div className="coin-medallion h-10 w-10">
                <Medallion denom={parts[0].denom} />
              </div>
            </div>
          </div>
        </div>

        <div role="status" aria-live="polite" className="sr-only">
          {label.title} — {formatCoinFull(current.amountCp)} added to your wallet
        </div>
      </div>

      {ring && (
        <div
          className="coin-land-ring pointer-events-none fixed z-50 rounded-md"
          style={{ left: ring.left, top: ring.top, width: ring.width, height: ring.height }}
          aria-hidden="true"
        />
      )}
    </>
  );
}

/**
 * ⚠️ MOUNTED ONCE, at the app root, beside the XP toast. The watcher has to run
 * whether or not a card is showing — it holds the realtime subscription — so it
 * lives here rather than inside the card it eventually opens.
 */
export function CoinRewardHost() {
  useCoinGrants();
  return <CoinRewardToast />;
}
